import { CherryBankControllerV6ApiFactory } from "@eu.mdotm.api/sphere";
import { getApiGen } from "$root/api/factory";
import AuthorizationGuard, { hasAccess } from "$root/components/AuthorizationGuard";
import { useCrumbs } from "$root/components/Crumbs/useCrumbs";
import type { DefaultTemplateId } from "$root/components/EvolvedPrint/configuration/hooks/useExtractReports";
import { OutlookWidgetData } from "$root/components/OutlookWidgetData";
import { PageDownloadMenu } from "$root/components/PageDownloadMenu";
import { PageHeader } from "$root/components/PageHeader";
import { typedUrlForRoute } from "$root/components/PlatformRouter/RoutesDef";
import { downloadSinglePdf } from "$root/functional-areas/pdf";
import { useUserValue } from "$root/functional-areas/user";
import { formatDate, formatDateTime } from "$root/localization/formatters";
import { axiosExtract } from "$root/third-party-integrations/axios";
import { downloadFileResponse } from "$root/utils";
import WidgetsMapper from "$root/widgets-architecture/layout/WidgetsMapper";
import { useUnsafeUpdatedRef } from "@mdotm/utils/react-extra";
import { useTranslation } from "react-i18next";
import type { DropdownDropdownItem } from "@mdotm/shadcn/mdotm/components";

async function downloadPositioningPdfReport() {
	const reportName = `Cherry_Bank_Report_${formatDate(new Date())}`;
	await downloadSinglePdf(reportName, {
		templateId: "custom-report" as DefaultTemplateId,
		name: reportName,
		isMarket: true,
	});
}

async function downloadPositioningXlsxReport() {
	const cherryBankControllerV6Api = getApiGen(CherryBankControllerV6ApiFactory);
	const template = await axiosExtract(cherryBankControllerV6Api.retrieveReport());
	downloadFileResponse(template, {
		fileName: `Cherry_Bank_Report_${formatDateTime(new Date())}.xlsx`,
	});
}

const CustomReports = (): React.JSX.Element => {
	const { t } = useTranslation();
	const user = useUserValue();
	const crumbs = useCrumbs();
	const downloadPfRef = useUnsafeUpdatedRef(downloadPositioningPdfReport);
	const downloadXlsRef = useUnsafeUpdatedRef(downloadPositioningXlsxReport);

	return (
		<AuthorizationGuard requiredService="CUSTOM_REPORT_CB1" placeholder={t("NOT_AUTHORIZED")}>
			{() => (
				<>
					<PageHeader
						crumbs={crumbs.marketInsights("Custom reports")}
						subTitle={<OutlookWidgetData />}
						titleAction={
							<PageDownloadMenu
								area="custom-report"
								actions={[
									[
										"",
										[
											{
												icon: "Dowload" as const,
												onClickAsync: () => downloadXlsRef.current(),
												label: "XLS report",
												"data-qualifier": "CustomReports/DropdownMenu/DropdownItem(XLSReport)",
											},
											{
												icon: "Dowload" as const,
												onClickAsync: () => downloadPfRef.current(),
												label: "PDF report",
												"data-qualifier": "CustomReports/DropdownMenu/DropdownItem(PDFReport)",
											},
										],
									],
									...(!hasAccess(user, { requiredRoles: ["ROOT"] })
										? []
										: [
												[
													"ROOT FEATURES",
													[
														{
															icon: "Expand" as const,
															onClick: () => {
																window.open(typedUrlForRoute("Report/Demo", { templateId: "custom-report" }), "_blank");
															},
															label: "Vertical Report",
															"data-qualifier": "CustomReports/DropdownMenu/DropdownItem(Preview)",
														},
													],
												] satisfies [title: string, items: DropdownDropdownItem[]],
											]),
								]}
							/>
						}
					/>
					<div className="relative">
						<WidgetsMapper
							widgetName="AssetClassOverviewBlock"
							style={{ paddingBottom: "1rem", marginBottom: "1rem", minHeight: 0 }}
							span={3}
						/>
						<WidgetsMapper
							widgetName="CustomOutlookBlock"
							style={{ paddingBottom: "1rem", maxHeight: "none" }}
							span={3}
						/>
					</div>
				</>
			)}
		</AuthorizationGuard>
	);
};

export default CustomReports;
